import type { TrackingDevicesResult } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { createDeviceMapping, listIntegrationDevices } from '../gps.api';

export interface DeviceMappingFormValues {
  deviceId: string;
  truckId: string;
  /** Picked as a date; blank means from now. */
  effectiveFrom?: { toISOString(): string } | null;
}

export type DevicesState = { status: 'loading' } | { status: 'loaded'; result: TrackingDevicesResult } | { status: 'error' };

/** Putting one of this integration's devices on a truck: the vendor's device list for the picker, and the save. */
export function useDeviceMappingSheet(integrationId: string, onDone: () => void) {
  const toast = useToast();
  const [devices, setDevices] = useState<DevicesState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listIntegrationDevices(integrationId)
      .then((result) => !cancelled && setDevices({ status: 'loaded', result }))
      .catch(() => !cancelled && setDevices({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  async function save(values: DeviceMappingFormValues): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const mapping = await createDeviceMapping({
        integrationId,
        deviceId: values.deviceId.trim(),
        truckId: values.truckId,
        effectiveFrom: values.effectiveFrom?.toISOString(),
      });
      toast.success(`${mapping.deviceId} mapped to ${mapping.truckRegistration}`);
      onDone();
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save the mapping. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return { devices, submitting, error, save };
}
